import type { UploadContext } from "@upsquad/shared";
import { Check, Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageCropModal } from "@/src/common/components/image-crop-modal";
import { Avatar } from "@/src/common/components/ui";
import { compressImage } from "@/src/common/lib/compress-image";
import { toastService } from "@/src/common/services/toast.service";
import { usePresignUploadMutation } from "@/src/common/services/upload-http.service";

interface PhotoUploadProps {
	value?: string | null;
	onChange: (url: string | null) => void;
	context: UploadContext;
	variant?: "avatar" | "logo";
	fallbackText?: string;
}

export function PhotoUpload({ value, onChange, context, variant = "avatar", fallbackText = "?" }: PhotoUploadProps) {
	const { t } = useTranslation("onboarding");
	const [presignUpload] = usePresignUploadMutation();
	const [uploading, setUploading] = useState(false);
	const [fileName, setFileName] = useState<string | null>(null);
	const [cropSrc, setCropSrc] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (inputRef.current) inputRef.current.value = "";
		if (!file) return;

		if (!["image/png", "image/jpeg"].includes(file.type)) {
			toastService.error(t("upload_error_type"));
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			toastService.error(t("upload_error_size"));
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setCropSrc(objectUrl);
		setFileName(file.name);
	};

	const handleCropComplete = async (croppedFile: File) => {
		if (cropSrc) URL.revokeObjectURL(cropSrc);
		setCropSrc(null);
		setUploading(true);

		try {
			const compressed = await compressImage(croppedFile);
			const result = await presignUpload({
				file_name: compressed.name,
				content_type: "image/jpeg",
				size: compressed.size,
				context,
			}).unwrap();

			if (!result) throw new Error("Failed to get presigned URL");

			const uploadResponse = await fetch(result.upload_url, {
				method: "PUT",
				body: compressed,
				headers: { "Content-Type": "image/jpeg" },
			});

			if (!uploadResponse.ok) throw new Error(`Upload failed with status ${uploadResponse.status}`);

			onChange(result.public_url);
		} catch {
			toastService.error(t("upload_error_failed"));
			setFileName(null);
			onChange(null);
		} finally {
			setUploading(false);
		}
	};

	const handleCropCancel = () => {
		if (cropSrc) URL.revokeObjectURL(cropSrc);
		setCropSrc(null);
		setFileName(null);
	};

	const handleRemove = () => {
		onChange(null);
		setFileName(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	if (variant === "logo") {
		return (
			<div>
				<input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFileSelect} />
				{!value && !uploading ? (
					<button
						type="button"
						onClick={() => inputRef.current?.click()}
						className="w-full flex items-center gap-3 p-4 border-2 border-dashed border-default-300 rounded-xl hover:border-accent transition-colors"
					>
						<Upload className="w-5 h-5 text-foreground/40" />
						<div className="text-left">
							<p className="text-sm font-medium">{t("upload_logo_label")}</p>
							<p className="text-xs text-foreground/40">{t("upload_logo_hint")}</p>
						</div>
					</button>
				) : (
					<div className="w-full flex items-center justify-between p-4 border border-default-200 rounded-xl">
						<div className="flex items-center gap-3">
							<Upload className="w-5 h-5 text-foreground/40" />
							<div>
								<p className="text-sm font-medium">{fileName}</p>
								<p className="text-xs text-foreground/40">{uploading ? t("uploading") : t("upload_success")}</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							{uploading ? (
								<Loader2 className="w-5 h-5 text-accent animate-spin" />
							) : (
								<>
									<Check className="w-5 h-5 text-success" />
									<button type="button" onClick={handleRemove}>
										<X className="w-4 h-4 text-foreground/40 hover:text-foreground" />
									</button>
								</>
							)}
						</div>
					</div>
				)}
				{cropSrc && <ImageCropModal imageSrc={cropSrc} aspectRatio={1} onCropComplete={handleCropComplete} onCancel={handleCropCancel} />}
			</div>
		);
	}

	return (
		<div className="flex items-center gap-4">
			<input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleFileSelect} />
			<button type="button" onClick={() => inputRef.current?.click()} className="relative group">
				<Avatar size="lg" className="w-16 h-16">
					<Avatar.Image src={value ?? undefined} alt="Profile" />
					<Avatar.Fallback>{fallbackText}</Avatar.Fallback>
				</Avatar>
				<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
					{uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Upload className="w-5 h-5 text-white" />}
				</div>
			</button>
			<div>
				<p className="text-sm font-medium">{t("photo_label")}</p>
				<p className="text-xs text-foreground/40">{t("photo_hint")}</p>
			</div>
			{cropSrc && <ImageCropModal imageSrc={cropSrc} aspectRatio={1} onCropComplete={handleCropComplete} onCancel={handleCropCancel} />}
		</div>
	);
}
